import React, { useState } from "react";
import { Link, useSearchParams, useNavigate } from "react-router-dom";
import { api, formatApiErrorDetail } from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Check } from "lucide-react";

export default function ResetPassword() {
  const [params] = useSearchParams();
  const token = params.get("token") || "";
  const navigate = useNavigate();
  const [pw, setPw] = useState("");
  const [confirm, setConfirm] = useState("");
  const [error, setError] = useState("");
  const [done, setDone] = useState(false);
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    if (pw.length < 6) return setError("Password must be at least 6 characters.");
    if (pw !== confirm) return setError("Passwords don't match.");
    setLoading(true);
    try {
      await api.post("/auth/reset-password", { token, new_password: pw });
      setDone(true);
      setTimeout(() => navigate("/admin/login", { replace: true }), 2500);
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-[var(--charcoal)] px-5">
      <div className="w-full max-w-md">
        <div className="text-center mb-8">
          <img src="/assets/seraphina-logo-hero.png" alt="Seraphina" className="h-16 w-16 object-contain mx-auto mb-4" />
          <h1 className="font-display text-4xl text-[#F3E9DC]">Choose a New Password</h1>
        </div>

        {!token ? (
          <div className="bg-[#46413b] rounded-2xl p-8 border border-[#5a544c] text-center text-[#F3E9DC]">
            <p>This reset link is missing its token. Please request a new one.</p>
            <Link to="/admin/forgot" className="btn-gold rounded-full px-8 py-3 inline-block mt-6">Request New Link</Link>
          </div>
        ) : done ? (
          <div data-testid="reset-done" className="bg-[#46413b] rounded-2xl p-8 border border-[#5a544c] text-center text-[#F3E9DC]">
            <p className="flex items-center justify-center gap-2 text-[var(--gold-soft)]"><Check size={18} /> Password updated!</p>
            <p className="text-sm text-[#c9bba9] mt-2">Redirecting you to the login page…</p>
          </div>
        ) : (
          <form onSubmit={submit} data-testid="reset-form" className="bg-[#46413b] rounded-2xl p-8 border border-[#5a544c]">
            <label className="eyebrow text-[var(--gold-soft)]">New Password</label>
            <Input data-testid="reset-password" type="password" value={pw} onChange={(e) => { setPw(e.target.value); setError(""); }}
              className="mt-2 mb-5 bg-[#3B3733] border-[#5a544c] text-[#F3E9DC]" placeholder="At least 6 characters" />
            <label className="eyebrow text-[var(--gold-soft)]">Confirm New Password</label>
            <Input data-testid="reset-confirm" type="password" value={confirm} onChange={(e) => { setConfirm(e.target.value); setError(""); }}
              className="mt-2 mb-6 bg-[#3B3733] border-[#5a544c] text-[#F3E9DC]" placeholder="Repeat new password" />
            {error && <p data-testid="reset-error" className="text-red-300 text-sm mb-4">{error}</p>}
            <button data-testid="reset-submit" type="submit" disabled={loading} className="btn-gold rounded-full w-full py-3.5 disabled:opacity-60">
              {loading ? "Saving…" : "Update Password"}
            </button>
          </form>
        )}
      </div>
    </div>
  );
}
